import React, { useEffect, useState } from "react";
import { IonContent, IonPage, useIonRouter } from "@ionic/react";
import Header from "../../../components/Header";
import AdherenceSummaryCard from "./AdheranceSummary";
import { EnhancedTitrationCard } from "./TitrationSummary";
import CurveAnalysisCard from "./CurveAnalysis";
import { useBLE } from "../../../hooks/useBle";
import { RequestMessageType } from "../../../contexts/types";
import TherapyDataCard from "./TherapyDataCard";
import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";

const TherapyInsights: React.FC = () => {
  // Simulated BLE context states

  const [fullscreenCard, setFullscreenCard] = useState<string | null>(null);
  const router = useIonRouter();
  const {
    handleFindDevice,
    handleSyncAdherenceSummary,
    adherenceData,
    adherenceLoading,
    titrationData,
    titrationLoading,
    handleSyncTitration,
    curveData,
    curveLoading,
    handleSyncCurveAnalysis,
    isConnected,
    askForSelectiveData,
    adherenceBackgroundLoading,
    device,
    parseDeviceString,
  } = useBLE();

  const isClearDevice =
    isConnected &&
    parseDeviceString(device?.device.name || "").deviceType === "BiWaze® Clear";

  useEffect(() => {
    const fetchData = async () => {
      // console.log("Fetching data... rendering", new Date().toISOString());
      const askLogs =
        !adherenceLoading &&
        !titrationLoading &&
        !curveLoading &&
        !adherenceBackgroundLoading;
      const requestMSGTypes: RequestMessageType[] = isClearDevice
        ? [RequestMessageType.ADHERENCE]
        : [
            RequestMessageType.MAPS_FOR_LOGS,
            RequestMessageType.ADHERENCE,
            RequestMessageType.TITRATION,
            RequestMessageType.HISTORICAL_CURVES,
          ];
      if (!isClearDevice && askLogs) {
        requestMSGTypes.push(RequestMessageType.LOGS);
      }
      await askForSelectiveData(requestMSGTypes);
    };
    if (isConnected) {
      fetchData();
    }
  }, [
    isConnected,
    adherenceLoading,
    titrationLoading,
    curveLoading,
    adherenceBackgroundLoading,
  ]);

  useEffect(() => {
    const lockOrientation = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          await ScreenOrientation.lock({ orientation: "portrait" });
        } catch (error) {
          console.warn("Failed to lock orientation:", error);
        }
      }
    };
    lockOrientation();
  }, []);

  // console.log("Rendering TherapyInsights", new Date().toISOString());

  return (
    <IonPage
      className="remote-page"
      style={{
        "--ion-background-color": "#000000",
        "--ion-text-color": "#ffffff",
      }}
    >
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle="Therapy Insights"
        showBackButton={true}
        showBtConnectionStatus={true}
        handleFindDevice={handleFindDevice}
        onBackButtonClick={() => {
          router.push("/remote");
        }}
      />

      <IonContent
        style={{
          "--background": "#000000",
          "--padding-top": "0px",
        }}
      >
        {/* Container with left border line */}
        <div
          style={{
            position: "relative",
            paddingLeft: "18px", // Add some spacing from the line
            minHeight: "100%", // Ensure it spans full height
          }}
        >
          {/* Gradient line */}
          <div
            style={{
              position: "absolute",
              left: "0",
              top: "0",
              bottom: "0",
              width: "4px",
              background: "linear-gradient(90deg, #01b7c4 0%, #00acd7 100%)",
            }}
          />

          <div
            style={{
              fontFamily:
                "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
              color: "#ffffff",
              padding: "20px",
            }}
          >
            <AdherenceSummaryCard
              data={adherenceData}
              isLoading={adherenceLoading}
              isClearDevice={isClearDevice}
              onSync={async (dateRange) =>
                await handleSyncAdherenceSummary(dateRange)
              }
            />

            {!isClearDevice && (
              <>
                <EnhancedTitrationCard
                  data={titrationData}
                  isLoading={titrationLoading}
                  onSync={async (session) => await handleSyncTitration(session)}
                />

                <CurveAnalysisCard
                  data={curveData}
                  isLoading={curveLoading}
                  onSync={async (session, pressure, flow, volume) =>
                    await handleSyncCurveAnalysis(
                      session,
                      pressure,
                      flow,
                      volume
                    )
                  }
                  minHandleSeparationPx={10}
                />
                <TherapyDataCard
                  onExpand={() => setFullscreenCard("therapyData")}
                />
              </>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default TherapyInsights;
